import { useCallback, useEffect, useRef } from 'react';
import { patientApi } from '../services/api';

export interface UseDwellTimeOptions {
  patientId: string | null | undefined;
  isActive: boolean;
  // Minimum seconds before the close-event will POST /session/patient/action/log_dwell.
  // Defaults to 5s — matches the legacy PatientDetail floor.
  minimumSeconds?: number;
  // Skip dwell tracking entirely (e.g., for non-onboarded patients). Defaults to false.
  // Disables both server logging AND the local snapshot — use logToServer if you only
  // want to suppress the patient_actions row but still display elapsed seconds.
  skip?: boolean;
  // PHA-533: when false, track elapsed seconds locally for `dwellSecondsSnapshot()` but
  // do NOT POST /log_dwell on close or via pagehide. Used by the Smart Hub AckDrawer,
  // where the canonical billing event is the 30s "Smart Hub Acknowledgement" written by
  // the ack endpoints — an extra "Active Care Management Time" row from drawer dwell
  // would double-count the same review window. Defaults to true.
  logToServer?: boolean;
  // PHA-710: when true, pause dwell accumulation (banks any in-progress time) even if
  // the document is visible and focused. Used to pause dwell while the session-timeout
  // warning modal is shown or the screen is locked, so that idle/warning time is not
  // billed as care time. Defaults to false.
  paused?: boolean;
}

export interface UseDwellTimeApi {
  // Returns elapsed seconds since the dwell session opened (0 if not active).
  // Snapshot — does not change reactively.
  dwellSecondsSnapshot: () => number;
}

/**
 * Centralized dwell-time tracking hook.
 *
 * When `isActive` becomes true, records the open timestamp. When `isActive`
 * goes false (or the component unmounts), POSTs the duration to
 * /session/patient/action/log_dwell — but only if `dwell >= minimumSeconds`.
 *
 * PHA-676 / PHA-711: Only VISIBLE time is counted. The hook pauses
 * accumulation when the document is hidden (tab switched, minimized) and
 * resumes when it becomes visible again. Window focus/blur does NOT gate
 * accumulation — a clinician reading a chart on a visible-but-unfocused
 * window (e.g. second monitor, glancing at another app) should have that
 * time counted. The residual over-count risk is bounded by the PHA-710
 * 5-min idle lock (session-warning modal sets `paused=true`).
 *
 * PHA-711: Removed window blur/focus gating that caused ~0s accrual for
 * deliberate reading on visible-but-unfocused windows (second monitor /
 * glancing elsewhere). Visibility-only gating is the correct boundary.
 *
 * Used by both `PatientDetail` (legacy "dwell in patient record" billing
 * tracking, 5s minimum) and the Smart Hub `AckDrawer` (PHA-441 review-time
 * tracking, 30s+ org-configurable minimum).
 */
export function useDwellTime(opts: UseDwellTimeOptions): UseDwellTimeApi {
  const {
    patientId,
    isActive,
    minimumSeconds = 5,
    skip = false,
    logToServer = true,
    paused = false,
  } = opts;

  // openTimeRef: when non-null, the clock is running from this timestamp.
  // Set to null when paused (document hidden or PHA-710 paused prop) or not active.
  const openTimeRef = useRef<Date | null>(null);
  // accumulatedMsRef: visible milliseconds banked during prior run segments
  // before a pause. Reset to 0 on each new patient/activation.
  const accumulatedMsRef = useRef<number>(0);
  const loggedRef = useRef<boolean>(false);

  /**
   * Returns total visible milliseconds: already-banked + current running segment.
   */
  const focusedMs = useCallback((): number => {
    const banked = accumulatedMsRef.current;
    const running = openTimeRef.current ? Date.now() - openTimeRef.current.getTime() : 0;
    return banked + running;
  }, []);

  const flush = useCallback(async () => {
    if (skip || !logToServer) return;
    if (!patientId) return;
    if (loggedRef.current) return;
    // Must have some accumulated time or an active running segment.
    if (accumulatedMsRef.current === 0 && !openTimeRef.current) return;

    const totalMs = focusedMs();
    const seconds = totalMs / 1000;
    if (seconds < minimumSeconds) {
      // Reset so a re-open starts a fresh session.
      openTimeRef.current = null;
      accumulatedMsRef.current = 0;
      loggedRef.current = false;
      return;
    }
    loggedRef.current = true;

    const now = new Date();
    const effectiveBegin = new Date(now.getTime() - totalMs);

    // Stop the running segment before the async call so concurrent flushes
    // (e.g. pagehide racing with unmount) don't double-count.
    openTimeRef.current = null;
    accumulatedMsRef.current = 0;

    try {
      await patientApi.logDwellTime({
        action_begin_ts: effectiveBegin.toISOString(),
        patient_id: patientId,
        action_end_ts: now.toISOString(),
      });
    } catch (err) {
      // Match legacy PatientDetail behavior — log, don't surface to user.
      // eslint-disable-next-line no-console
      console.warn('useDwellTime: log_dwell failed', err);
    }
  }, [patientId, minimumSeconds, skip, logToServer, focusedMs]);

  // PHA-676 / PHA-711: Pause accumulation when document is hidden (tab switched,
  // minimized). Window blur/focus does NOT gate accrual — visible-but-unfocused
  // windows (second monitor, glancing elsewhere) should count.
  // PHA-710: Also pause when the `paused` prop is true (e.g. session warning modal shown).
  // The guards (openTimeRef null-check on pause, !openTimeRef on resume) prevent
  // double-accounting. The cleanup removes listeners before re-adding so no
  // duplicate listeners accumulate when any dependency (isActive, patientId, paused) changes.
  useEffect(() => {
    if (skip) return undefined;

    const pause = () => {
      // Only bank time if the clock is currently running.
      if (openTimeRef.current) {
        accumulatedMsRef.current += Date.now() - openTimeRef.current.getTime();
        openTimeRef.current = null;
      }
    };

    const resume = () => {
      // Only restart if isActive, not already running, document is visible,
      // AND the external paused flag is clear. Focus state is intentionally
      // NOT checked (PHA-711: visible-but-unfocused time counts).
      if (isActive && patientId && !openTimeRef.current
          && !document.hidden && !paused) {
        openTimeRef.current = new Date();
      }
    };

    // PHA-710: if the session warning just became active, bank any running time.
    if (paused) {
      pause();
    } else {
      // Session warning dismissed — try to resume if conditions allow.
      resume();
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pause();
      } else {
        resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // isActive / patientId are needed by resume() — include them so resume()
    // always sees the current values when an event fires.
    // paused is included so the effect re-runs when the session warning toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, patientId, skip, paused]);

  useEffect(() => {
    if (skip) return;
    if (isActive && patientId) {
      // Reset state for a fresh session (new patient or re-activation).
      accumulatedMsRef.current = 0;
      loggedRef.current = false;
      // Start the clock if the document is currently visible.
      // Focus state is intentionally NOT checked (PHA-711).
      if (!document.hidden) {
        openTimeRef.current = new Date();
      } else {
        openTimeRef.current = null;
      }
      return () => {
        // isActive flipped to false (or component unmounting): flush.
        flush();
        // Ensure clean state for the next patient/activation.
        openTimeRef.current = null;
        accumulatedMsRef.current = 0;
        loggedRef.current = false;
      };
    }
    return undefined;
  }, [isActive, patientId, skip, flush]);

  // PHA-565: flush on browser close / refresh / sign-out via pagehide +
  // sendBeacon. fetch() does not survive page unload, so we POST a tiny JSON
  // blob via the Beacon API (which the browser guarantees to deliver). Without
  // this, a user who closes the browser while a chart is open loses the dwell
  // entirely. The beacon fires only when there's accumulated visible time
  // that meets the minimum.
  // PHA-676 / PHA-711: uses focusedMs() so only visible (non-hidden) time is
  // reported, not wall-clock.
  useEffect(() => {
    if (skip || !logToServer || !patientId) return undefined;
    const handler = () => {
      if (loggedRef.current) return;
      const totalMs = focusedMs();
      if (totalMs / 1000 < minimumSeconds) return;
      loggedRef.current = true;
      try {
        const now = new Date();
        const effectiveBegin = new Date(now.getTime() - totalMs);
        const blob = new Blob(
          [
            JSON.stringify({
              action_begin_ts: effectiveBegin.toISOString(),
              patient_id: patientId,
              action_end_ts: now.toISOString(),
            }),
          ],
          { type: 'application/json' },
        );
        navigator.sendBeacon('/session/patient/action/log_dwell', blob);
      } catch {
        // Best effort; nothing we can do during unload.
      }
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [patientId, skip, minimumSeconds, logToServer, focusedMs]);

  const dwellSecondsSnapshot = useCallback(() => {
    // Include banked accumulated time so callers (e.g. AckDrawer display) see
    // the correct elapsed value even while the clock is paused.
    return Math.floor(focusedMs() / 1000);
  }, [focusedMs]);

  return { dwellSecondsSnapshot };
}

export default useDwellTime;
